import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SAP_PUSH_STEP_LABELS, type ResolvedSapField, type SapPushStep } from '@hcc/types';
import { Pill } from '../../../../components/ui/Pill';

const ORIGIN_LABEL: Record<ResolvedSapField['origin'], string> = {
  form: 'form',
  person: 'person record',
  employment_person: 'override',
  employment_role: 'role default',
  missing: 'missing',
};

/**
 * What SAP will receive, field by field, with where each value came
 * from. This admin-only view intentionally shows the decrypted values:
 * reviewing the exact payload before an irreversible LIVE hire is the
 * point of the screen.
 */
export function SapReviewCard({ fields, personId }: { fields: ResolvedSapField[]; personId?: string }) {
  const groups = useMemo(() => {
    const byStep = new Map<SapPushStep, ResolvedSapField[]>();
    for (const f of fields) byStep.set(f.step, [...(byStep.get(f.step) ?? []), f]);
    return [...byStep.entries()];
  }, [fields]);

  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="border-b border-line/10 px-5 py-3">
        <h3 className="text-sm font-semibold">What SAP will receive</h3>
      </div>
      <div className="divide-y divide-divider/10">
        {groups.map(([step, stepFields]) => (
          <div key={step} className="px-5 py-3">
            <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">
              {SAP_PUSH_STEP_LABELS[step]}
            </div>
            <dl className="mt-2 space-y-1.5">
              {stepFields.map((f) => (
                <div key={f.field} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                  <dt className="w-56 shrink-0 text-fg-muted">
                    {f.label}
                    {f.required ? <span className="ml-0.5 text-danger">*</span> : null}
                  </dt>
                  <dd className="flex min-w-0 flex-1 flex-wrap items-baseline gap-2">
                    {f.value !== null ? (
                      <span className="break-all font-medium">{f.value}</span>
                    ) : (
                      <span className="text-fg-dim">—</span>
                    )}
                    <Pill tone={f.origin === 'missing' ? (f.required ? 'danger' : 'neutral') : 'info'}>
                      {ORIGIN_LABEL[f.origin]}
                    </Pill>
                    {f.problem ? (
                      <span className="text-xs text-danger-soft-fg">{f.problem}</span>
                    ) : null}
                    {f.problem && f.origin === 'form' && personId ? (
                      // A broken form answer is fixable in place: HR can
                      // correct it (audited) on the Submissions page.
                      <Link
                        to={`/onboarding/submissions/${personId}`}
                        className="text-xs text-accent hover:underline"
                      >
                        Fix in submissions
                      </Link>
                    ) : null}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}
